import { useMemo, type ComponentType } from "react";

import clsx from "clsx";
import { type DocumentNode } from "graphql";

import { type Option } from "@/types/react-select";
import { useCachedQuery } from "@components/hooks";
import { Tooltip } from "@components/tooltip-container";
import { MultiSelect, Select } from "@components/v2";
import { TextInput } from "@components/v2/inputs";
import { useNorthbeamQuery } from "@hooks/http";
import {
  ValueComparisonType,
  type FetchCustomerTagOptions,
  type FetchOrderDiscountCodeOptions,
  type FetchOrderProductOptions,
  type FetchOrderTagOptions,
  type FetchOrderTypeOptions,
  type FetchSourceNameOptions,
} from "@north-beam/nb-client-side";
import { CustomerTypes } from "@north-beam/nb-common";
import { SOURCE_NAME_TAG } from "@pages/orders/orders-table/constant";
import {
  FETCH_CUSTOMER_TAG_OPTIONS,
  FETCH_DISCOUNT_CODES_OPTIONS,
  FETCH_ORDER_PRODUCT_OPTIONS,
  FETCH_ORDER_TAG_OPTIONS,
  FETCH_ORDER_TYPE_OPTIONS,
  FETCH_SOURCE_NAME_OPTIONS,
} from "@pages/settings/queries";
import { SUBSCRIPTION_TYPES } from "@pages/subscriptions-order-tags/subscription-order-tags";
import { generateAnonBase64Hash } from "@utils/anonymous";
import { usePrefs } from "@utils/client-side-preferences";

import {
  type FilterComparison,
  type Filter,
  type FilterList,
  type FilterValue,
} from "./filter-modal";
import { useDateRange } from "./hooks/use-date-range";

type FiltersControlProps = {
  filter: Filter;
  onChange: (filter: Filter) => void;
};

export const FiltersControl = ({ filter, onChange }: FiltersControlProps) => {
  const filtersMapping = {
    customerId: TextFilter,
    customerTags: CustomerTagsFilter,
    discountCodes: DiscountCodesFilter,
    discountValue: ComparisonFilter,
    attributed: AttributedFilter,
    numberOfOriginalItems: ComparisonFilter,
    numberOfTouchpoints: ComparisonFilter,
    orderId: TextFilter,
    orderNumber: TextFilter,
    orderTags: OrderTagsFilter,
    orderTypes: OrderTypesFilter,
    orderValue: ComparisonFilter,
    products: ProductFilter,
    refundValue: ComparisonFilter,
    shippingValue: ComparisonFilter,
    sourceName: SourceNameFilter,
    subscriptions: SubscriptionsFilter,
    taxValue: ComparisonFilter,
  };

  const FilterComponent = filtersMapping[
    filter.column as keyof typeof filtersMapping
  ] as ComponentType<FiltersControlProps>;
  return FilterComponent ? (
    <FilterComponent filter={filter} onChange={onChange} />
  ) : null;
};

type MultiSelectFilterProps = {
  filter: FilterList;
  onChange: (filter: FilterList) => void;
  options: Option[];
  disabled?: boolean;
  dropdownClassName?: string;
  shouldAnon?: boolean;
};

const MultiSelectFilter = ({
  filter,
  onChange,
  options,
  disabled,
  dropdownClassName,
  shouldAnon,
}: MultiSelectFilterProps) => {
  const prefs = usePrefs();
  const doAnon = prefs.isDemoMode;
  const filtersCount = filter.value?.length || 0;
  const internalOptions = useMemo(
    () =>
      shouldAnon
        ? options.map((o) => ({
            label: generateAnonBase64Hash({
              value: o.label,
              prefix: filter.column,
              doAnon,
            }),
            value: generateAnonBase64Hash({
              value: o.value,
              prefix: filter.column,
              doAnon,
            }),
          }))
        : options,
    [doAnon, filter.column, options, shouldAnon],
  );
  const selectedOptions = useMemo(() => {
    const valueFiltersSet = new Set(filter.value);
    return internalOptions.filter((o) => valueFiltersSet.has(o.value));
  }, [filter.value, internalOptions]);

  return (
    <>
      <MultiSelect
        hasSelectAll
        className=""
        buttonClassName="w-60"
        options={internalOptions}
        values={filter.value ?? []}
        onChange={(value) => onChange({ ...filter, value })}
        disabled={disabled}
        dropdownClassName={dropdownClassName}
        hasSearch
      />
      <Tooltip
        content={
          <div className="w-60 max-h-60 overflow-y-auto break-words">
            <div className="grid grid-cols-1 gap-2">
              {selectedOptions.map((v) => (
                <div key={v.value}>{v.label}</div>
              ))}
            </div>
          </div>
        }
        placement="top"
        noInfoCircle
        showTooltip={filtersCount > 0}
      >
        <span
          className={clsx("text-nowrap cursor-pointer", {
            "text-v2-secondary": filtersCount === 0,
            "text-v2-action": filtersCount > 0,
          })}
        >
          {filtersCount}
        </span>
      </Tooltip>
    </>
  );
};

function useGetOptions<Query>(query: DocumentNode) {
  const { dateRangeInterval } = useDateRange();

  const { data, loading } = useNorthbeamQuery<
    Query,
    { dateRange: { start: Date; end: Date } }
  >(query, {
    variables: {
      dateRange: {
        start: new Date(dateRangeInterval.start),
        end: new Date(dateRangeInterval.end),
      },
    },
  });
  const cachedData = useCachedQuery(data);
  return { loading, data: cachedData };
}

const ProductFilter = ({
  filter,
  onChange,
}: {
  filter: FilterList;
  onChange: (filter: FilterList) => void;
}) => {
  const { loading, data } = useGetOptions<FetchOrderProductOptions>(
    FETCH_ORDER_PRODUCT_OPTIONS,
  );
  return (
    <MultiSelectFilter
      filter={filter}
      onChange={onChange}
      options={(data?.me.products || []).map((p) => ({ value: p, label: p }))}
      disabled={loading}
      dropdownClassName="w-[300px]"
      shouldAnon={true}
    />
  );
};

const OrderTypesFilter = ({
  filter,
  onChange,
}: {
  filter: FilterList;
  onChange: (filter: FilterList) => void;
}) => {
  const { loading, data } = useGetOptions<FetchOrderTypeOptions>(
    FETCH_ORDER_TYPE_OPTIONS,
  );
  return (
    <MultiSelectFilter
      filter={filter}
      onChange={onChange}
      options={(data?.me.orderTypes || []).map((i) => ({
        value: CustomerTypes.contains(i) ? CustomerTypes.getLabel(i) : i,
        label: CustomerTypes.contains(i) ? CustomerTypes.getLabel(i) : i,
      }))}
      disabled={loading}
    />
  );
};

const OrderTagsFilter = ({
  filter,
  onChange,
}: {
  filter: FilterList;
  onChange: (filter: FilterList) => void;
}) => {
  const { loading, data } = useGetOptions<FetchOrderTagOptions>(
    FETCH_ORDER_TAG_OPTIONS,
  );
  return (
    <MultiSelectFilter
      filter={filter}
      onChange={onChange}
      options={(data?.me.orderTags || []).map((i) => ({ value: i, label: i }))}
      disabled={loading}
      dropdownClassName="w-[300px]"
      shouldAnon={true}
    />
  );
};

const SourceNameFilter = ({
  filter,
  onChange,
}: {
  filter: FilterList;
  onChange: (filter: FilterList) => void;
}) => {
  const { loading, data } = useGetOptions<FetchSourceNameOptions>(
    FETCH_SOURCE_NAME_OPTIONS,
  );
  return (
    <MultiSelectFilter
      filter={filter}
      onChange={onChange}
      options={(data?.me.sourceNames || []).map((i) => ({
        value: i,
        label: i.replace(new RegExp(`^${SOURCE_NAME_TAG}`), ""),
      }))}
      disabled={loading}
      dropdownClassName="w-[300px]"
    />
  );
};

const CustomerTagsFilter = ({
  filter,
  onChange,
}: {
  filter: FilterList;
  onChange: (filter: FilterList) => void;
}) => {
  const { loading, data } = useGetOptions<FetchCustomerTagOptions>(
    FETCH_CUSTOMER_TAG_OPTIONS,
  );
  return (
    <MultiSelectFilter
      filter={filter}
      onChange={onChange}
      options={(data?.me.customerTags || []).map((i) => ({
        value: i,
        label: i,
      }))}
      disabled={loading}
      dropdownClassName="w-[300px]"
      shouldAnon={true}
    />
  );
};

const SubscriptionsFilter = ({
  filter,
  onChange,
}: {
  filter: FilterList;
  onChange: (filter: FilterList) => void;
}) => {
  return (
    <MultiSelectFilter
      filter={filter}
      onChange={onChange}
      options={SUBSCRIPTION_TYPES}
      dropdownClassName="w-[200px]"
    />
  );
};

const DiscountCodesFilter = ({
  filter,
  onChange,
}: {
  filter: FilterList;
  onChange: (filter: FilterList) => void;
}) => {
  const { loading, data } = useGetOptions<FetchOrderDiscountCodeOptions>(
    FETCH_DISCOUNT_CODES_OPTIONS,
  );
  return (
    <MultiSelectFilter
      filter={filter}
      onChange={onChange}
      options={(data?.me.discountCodes || []).map((i) => ({
        value: i,
        label: i,
      }))}
      disabled={loading}
      dropdownClassName="w-[300px]"
      shouldAnon={true}
    />
  );
};

const AttributedFilter = ({
  filter,
  onChange,
}: {
  filter: FilterList;
  onChange: (filter: FilterList) => void;
}) => {
  return (
    <MultiSelectFilter
      filter={filter}
      onChange={onChange}
      options={[
        { value: "Yes", label: "Yes" },
        { value: "No", label: "No" },
      ]}
    />
  );
};

const ComparisonFilter = ({
  filter,
  onChange,
}: {
  filter: FilterComparison;
  onChange: (filter: FilterComparison) => void;
}) => {
  const comparisonOptions = [
    { value: ValueComparisonType.equalTo, label: "=" },
    { value: ValueComparisonType.greaterThan, label: ">" },
    { value: ValueComparisonType.greaterThanOrEqualTo, label: ">=" },
    { value: ValueComparisonType.lessThan, label: "<" },
    { value: ValueComparisonType.lessThanOrEqual, label: "<=" },
  ];

  return (
    <>
      <Select
        placeholder={
          <i className="fa-regular fa-sharp fa-chevron-down text-v2-primary" />
        }
        dropdownClassName="w-12"
        value={filter.comparisonOperator ?? null}
        onChange={(newValue: ValueComparisonType | null) => {
          if (newValue) {
            onChange({ ...filter, comparisonOperator: newValue });
          }
        }}
        options={comparisonOptions}
        hideCaret
      />
      <TextInput
        placeholder="Filter value..."
        className="w-60"
        type="number"
        value={filter.value || ""}
        onChange={(e) => onChange({ ...filter, value: e.target.value })}
      />
    </>
  );
};

const TextFilter = ({
  filter,
  onChange,
}: {
  filter: FilterValue;
  onChange: (filter: FilterValue) => void;
}) => (
  <TextInput
    placeholder="Filter value..."
    value={filter?.value || ""}
    onChange={(e) => onChange({ ...filter, value: e.currentTarget.value })}
  />
);
